import React from 'react';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';

interface ContactContent {
  type: 'contact';
  name: string;
  phone?: string;
  email?: string;
  vcard?: string;
}

interface ContactCardProps {
  content: ContactContent;
  isOwn?: boolean;
}

const GRADIENTS = [
  'linear-gradient(135deg, #f87171, #fb923c)',
  'linear-gradient(135deg, #60a5fa, #818cf8)',
  'linear-gradient(135deg, #34d399, #10b981)',
  'linear-gradient(135deg, #fbbf24, #f97316)',
  'linear-gradient(135deg, #a78bfa, #ec4899)',
  'linear-gradient(135deg, #22d3ee, #3b82f6)',
];

// Get initials for avatar (first + last name, 2 chars max)
const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0];
    const last = parts[parts.length - 1];
    return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
  }
  return trimmed.substring(0, 2).toUpperCase();
};

/**
 * Component to display contact messages (P5 Glass style)
 *
 * @example
 * <ContactCard isOwn={true} content={{
 *   type: 'contact',
 *   name: 'João Silva',
 *   phone: '+55 11 99999-9999',
 *   email: 'joao@email.com'
 * }} />
 */
export const ContactCard: React.FC<ContactCardProps> = ({ content, isOwn: _isOwn }) => {
  const { name, phone, email, vcard } = content;

  const handleCall = () => {
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    }
  };

  const handleMessage = () => {
    if (vcard) {
      // Download vCard file (keeps legacy behavior when vCard is available)
      const blob = new Blob([vcard], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name.replace(/\s+/g, '_')}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  };

  const hasCall = Boolean(phone);
  const hasMessage = Boolean(vcard || email);

  const initials = getInitials(name);
  const gradientIndex =
    Array.from(name).reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % GRADIENTS.length;
  const avatarGradient = GRADIENTS[gradientIndex];

  const contactName = name;
  const displayPhone = phone || email || '';

  return (
    <div data-testid="chat-message-contact">
      <div className="flex items-center gap-2.5">
        <div
          className="w-11 h-11 rounded-full flex-shrink-0 text-white font-bold text-base flex items-center justify-center"
          style={{ background: avatarGradient }}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold m-0 mb-0.5 truncate">{contactName}</p>
          <p className="text-[12px] opacity-70 m-0 tabular-nums">
            <PrivacyText>{displayPhone}</PrivacyText>
          </p>
        </div>
      </div>
      {(hasCall || hasMessage) && (
        <div
          className="flex gap-1.5 mt-2 pt-2"
          style={{ borderTop: '1px solid color-mix(in srgb, currentColor 15%, transparent)' }}
        >
          {hasCall && (
            <button
              type="button"
              onClick={handleCall}
              className="flex-1 py-1.5 text-[11.5px] font-semibold rounded-md border-0"
              style={{ background: 'transparent', color: 'currentColor' }}
              data-testid="chat-message-contact-call"
            >
              Ligar
            </button>
          )}
          {hasMessage && (
            <button
              type="button"
              onClick={handleMessage}
              className="flex-1 py-1.5 text-[11.5px] font-semibold rounded-md border-0"
              style={{ background: 'transparent', color: 'currentColor' }}
              data-testid="chat-message-contact-message"
            >
              Mensagem
            </button>
          )}
        </div>
      )}
    </div>
  );
};
